import { Box, HStack, Text } from '@chakra-ui/react';

interface VideoProgressBarProps {
  currentTime: number;
  duration: number;
  bufferedRanges: TimeRanges | null;
  isDragging: boolean;
  hoverTime: number | null;
  showPreview: boolean;
  isLocked: boolean;
  onProgressMouseMove: (e: React.MouseEvent<HTMLDivElement>) => void;
  onProgressMouseLeave: () => void;
  onProgressClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  formatTime: (seconds: number) => string;
  getProgressPercentage: () => number;
  getBufferedPercent: () => number;
}

const VideoProgressBar: React.FC<VideoProgressBarProps> = ({
  currentTime,
  duration,
  bufferedRanges,
  isDragging,
  hoverTime,
  showPreview,
  isLocked,
  onProgressMouseMove,
  onProgressMouseLeave,
  onProgressClick,
  formatTime,
  getProgressPercentage,
  getBufferedPercent
}) => {
  return (
    <HStack gap={4} align="center">
      <Box
        position="relative"
        flex="1"
        h="6px"
        bg="whiteAlpha.300"
        borderRadius="3px"
        cursor="pointer"
        onMouseMove={onProgressMouseMove}
        onMouseLeave={onProgressMouseLeave}
        onClick={onProgressClick}
      >
        {/* Hover preview tooltip */}
        {showPreview && hoverTime !== null && (
          <Box
            position="absolute"
            bottom="100%"
            left={`${(hoverTime / duration) * 100}%`}
            transform="translateX(-50%)"
            mb={2}
            bg="blackAlpha.900"
            color="white"
            px={2}
            py={1}
            borderRadius="md"
            fontSize="xs"
            fontWeight="medium"
            whiteSpace="nowrap"
            pointerEvents="none"
          >
            {formatTime(hoverTime)}
          </Box>
        )}
        {/* Buffered segments */}
        <Box
          position="absolute"
          top="0"
          left="0"
          h="100%"
          bg="whiteAlpha.500"
          borderRadius="3px"
          width={`${getBufferedPercent()}%`}
        />
        
        {/* Progress */}
        <Box
          position="absolute"
          top="0"
          left="0"
          h="100%"
          bg="purple.500"
          borderRadius="3px"
          width={`${duration ? (currentTime / duration) * 100 : 0}%`}
        />
        
        {/* Hover preview line */}
        {showPreview && hoverTime !== null && (
          <Box
            position="absolute"
            top="0"
            left={`${(hoverTime / duration) * 100}%`}
            w="2px"
            h="100%"
            bg="white"
            borderRadius="1px"
            transform="translateX(-50%)"
          />
        )}
        
        {/* Progress thumb */}
        <Box
          position="absolute"
          top="50%"
          left={`${duration ? (currentTime / duration) * 100 : 0}%`}
          w="14px"
          h="14px"
          bg="purple.500"
          borderRadius="50%"
          border="2px solid white"
          transform="translate(-50%, -50%)"
          opacity={isDragging ? 1 : 0}
          transition="opacity 0.2s"
          _hover={{ opacity: 1 }}
        />
        
        {/* Hover thumb */}
        {showPreview && hoverTime !== null && (
          <Box
            position="absolute"
            top="50%"
            left={`${(hoverTime / duration) * 100}%`}
            w="12px"
            h="12px"
            bg="white"
            borderRadius="50%"
            transform="translate(-50%, -50%)"
            opacity={0.8}
          />
        )}
      </Box>
      
      <HStack gap={2} minW="160px" justify="end">
        <Text color="white" fontSize="md" fontWeight="medium">
          {formatTime(currentTime)}
        </Text>
        <Text color="whiteAlpha.600" fontSize="md">
          / {formatTime(duration)}
        </Text>
        <Text color="whiteAlpha.500" fontSize="sm">
          ({getProgressPercentage()}%)
        </Text>
      </HStack>
    </HStack>
  );
};

export default VideoProgressBar;
